import { PropertyOperations } from "../../../hackle/property/PropertyOperations"
import { HackleUser, User } from "../model/model"
import { UserListener } from "./UserListener"
import { Synchronizer } from "../sync/Synchronizer"
import { ListenerRegistry } from "../listener/ListenerRegistry"

/**
 * Mode-agnostic user manager: owns the shared listener registry and the user-management contract.
 * Selected once at init by evaluation mode (local vs remote) and shared by every consumer.
 *
 * User-mutation methods return `Promise<void>` (fused): each concrete manager decides internally
 * whether a change requires synchronization — cohorts/target events for local, the per-user
 * workspace evaluation for remote — and the returned promise resolves when that work settles.
 *
 * Note: there is intentionally no `syncIfNeeded` on this contract. Deciding whether a change needs
 * a sync is owned by each concrete manager (invoked inside the fused mutations), not by callers.
 */
export abstract class UserManager extends ListenerRegistry<UserListener> implements Synchronizer {
  protected notifyUserUpdated(oldUser: User, newUser: User, timestamp: number): void {
    this.listeners.forEach((listener) => listener.onUserUpdated(oldUser, newUser, timestamp))
  }

  protected notifyPropertyOperations(user: User, operations: PropertyOperations, timestamp: number): void {
    this.listeners.forEach((listener) => listener.onPropertyOperations(user, operations, timestamp))
  }

  abstract get currentUser(): User

  abstract initialize(user?: User | null): void

  abstract hackleUser(user?: User | string): HackleUser

  abstract setUser(user: User): Promise<void>

  abstract setUserId(userId: string | undefined): Promise<void>

  abstract setDeviceId(deviceId: string): Promise<void>

  abstract updateUserProperties(operations: PropertyOperations): Promise<void>

  abstract resetUser(): Promise<void>

  abstract sync(): Promise<void>

  abstract close(): Promise<void>
}

export const isSameUser = (a: User | null, b: User): boolean => {
  if (a === null) return false
  return a.userId === b.userId && a.deviceId === b.deviceId
}

export const mergeUsers = (oldUser: User, newUser: User): User => {
  if (isSameUser(oldUser, newUser)) {
    return {
      ...newUser,
      properties: {
        ...oldUser.properties,
        ...newUser.properties
      }
    }
  }
  return newUser
}
